import type { ReactNode } from "react";

/**
 * Tasti a sola icona della barra del composer (Davide 2026-08-25: «usa icone
 * se non ti stanno, così è inutilizzabile»).
 *
 * Perché a icona e non a testo: misurato il 2026-08-25, con la colonna chat a
 * 588px la barra chiedeva 600px e **«Invia» finiva 32px fuori dalla colonna** —
 * il tasto per mandare la risposta non era raggiungibile, e «Traduci per il
 * cliente» si schiacciava da 134 a 65px. Le azioni accessorie a icona costano
 * 32px l'una invece di 61-134: la barra scese da 600 a ~465px.
 *
 * Dal 2026-09-02 ne restano DUE (allega, traduci la bozza): «AI suggerisci» e
 * «AI riscrivi» sono uscite con la pulizia dell'AI, quindi oggi la barra e'
 * ancora piu' stretta di cosi'. Il vincolo che ha deciso la forma resta valido
 * — «Invia» deve stare dentro la colonna anche a 588px.
 *
 * L'etichetta non sparisce, cambia canale: `aria-label` la dà al lettore di
 * schermo e `title` la mostra al passaggio del mouse. Un tasto a sola icona
 * senza nome accessibile sarebbe un peggioramento, non una compattazione.
 */
export function BottoneIcona({
  etichetta,
  icona,
  occupato = false,
  disabilitato = false,
  onClick,
}: {
  /** Nome accessibile E tooltip: è l'unico testo che questo tasto ha. */
  etichetta: string;
  icona: ReactNode;
  /** Azione in corso: mostra la rotella e lo dice con `aria-busy`. */
  occupato?: boolean;
  disabilitato?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabilitato || occupato}
      aria-label={etichetta}
      aria-busy={occupato}
      title={etichetta}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line-2 bg-card text-ink transition-colors hover:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:opacity-45"
    >
      {occupato ? <IconaAttesa /> : icona}
    </button>
  );
}

const TRATTO =
  "h-4 w-4 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]";

function IconaAttesa() {
  return (
    <svg className={`${TRATTO} animate-spin`} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-6.2-8.6" />
    </svg>
  );
}

export function IconaGraffetta() {
  return (
    <svg className={TRATTO} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

export function IconaMondo() {
  return (
    <svg className={TRATTO} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3.6 9h16.8M3.6 15h16.8" />
      <path d="M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </svg>
  );
}

export function IconaAereo() {
  return (
    <svg className={TRATTO} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

/** Scintilla: «Chiedi a Claude» (agente in Messaggi, 30/10/2026). */
export function IconaClaude() {
  return (
    <svg className={TRATTO} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 16l.7 1.8 1.8.7-1.8.7L19 21l-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  );
}
